import { useState, useRef } from "react";
import { motion } from "framer-motion";
import {
  TrendingUp,
  Globe,
  Smartphone,
  AtSign,
  Server,
  Zap,
  ArrowUpRight,
  Check,
} from "lucide-react";
import { FadeUp, SectionHeading } from "@/components/Reveal";
import InquiryDialog from "@/components/InquiryDialog";
import { SERVICES } from "@/data/site";

const ICONS = {
  "digital-marketing": TrendingUp,
  "website-development": Globe,
  "app-development": Smartphone,
  "domain-registration": AtSign,
  "shared-hosting": Server,
  "all-in-one": Zap,
};

const SpotCard = ({ children, className = "", ...rest }) => {
  const ref = useRef(null);
  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${e.clientX - r.left}px`);
    el.style.setProperty("--my", `${e.clientY - r.top}px`);
  };
  return (
    <div ref={ref} onMouseMove={onMove} className={`spot-card ${className}`} {...rest}>
      {children}
    </div>
  );
};

const Services = () => {
  const [dialogService, setDialogService] = useState(null);

  return (
    <section id="services" className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-12 lg:py-32" data-testid="services-section">
      <SectionHeading
        eyebrow="What we do"
        title="Six services. One growth engine."
        sub="Everything your business needs to get found, look world-class, and convert — without juggling five different vendors."
      />
      <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {SERVICES.map((s, i) => {
          const Icon = ICONS[s.id];
          return (
            <FadeUp key={s.id} delay={i * 0.08} className={s.span}>
              <motion.div whileHover={{ y: -6 }} transition={{ duration: 0.3 }} className="h-full">
                <SpotCard
                  data-testid={`service-card-${s.id}`}
                  className="group flex h-full flex-col rounded-2xl border border-white/10 bg-zinc-900/40 p-8 backdrop-blur-md"
                >
                  <div className="flex items-start justify-between">
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#D9A92E]/12 text-[#D9A92E] transition-all duration-300 group-hover:bg-[#D9A92E] group-hover:text-[#09090B] group-hover:shadow-[0_0_25px_rgba(217,169,46,0.5)]">
                      <Icon className="h-6 w-6" />
                    </span>
                    {s.featured && (
                      <span className="mono rounded-full bg-[#D9A92E]/12 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-[#E9C766]">
                        Most Complete
                      </span>
                    )}
                  </div>
                  <h3 className="mt-6 text-xl font-semibold tracking-tight text-zinc-50 sm:text-2xl">{s.name}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-400">{s.blurb}</p>
                  <ul className="mt-5 space-y-2">
                    {s.points.map((p) => (
                      <li key={p} className="flex items-center gap-2 text-sm text-zinc-400">
                        <Check className="h-3.5 w-3.5 text-[#D9A92E]" />
                        {p}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => setDialogService(s.id)}
                    data-testid={`service-inquire-button-${s.id}`}
                    className="mt-auto inline-flex items-center gap-1.5 pt-6 text-sm font-semibold text-[#E9C766] transition-colors duration-300 hover:text-[#D9A92E]"
                  >
                    Inquire Now
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </button>
                </SpotCard>
              </motion.div>
            </FadeUp>
          );
        })}
      </div>
      <InquiryDialog
        open={!!dialogService}
        onOpenChange={() => setDialogService(null)}
        service={dialogService || "general"}
      />
    </section>
  );
};

export default Services;
